<script setup>
import { computed, ref, watch } from 'vue';
import { getContrastingTextColor } from '@chatwoot/utils';
import { darken, getContrast } from 'color2k';
import { CsIcon } from 'dashboard/components/ui/commslayer';
import CsWidgetButton from 'widget/v2/components/CsWidgetButton.vue';
import { useStore } from 'widget/composables/store';
import { animateEnter } from 'widget/composables/useEnterAnimation';

const STAGGER_DELAY = 25;

defineProps({
  color: {
    type: String,
    default: '',
  },
  containedIn: {
    type: String,
    default: 'widget',
  },
});

const emit = defineEmits(['start-conversation']);

const store = useStore();
const widgetColor = computed(() => store.getters['appConfig/getWidgetColor']);

const textColor = computed(() => getContrastingTextColor(widgetColor.value));

const readableTextColor = computed(() => {
  const color = widgetColor.value || '#1f93ff';
  const contrastLevel = getContrast(color, '#fff');
  return contrastLevel <= 1.4 ? darken(color, 0.3) : color;
});

const iconBackgroundColor = computed(() => `${widgetColor.value}1A`);

// Animation refs
const buttonRef = ref(null);
const iconRef = ref(null);

// Watch for widget open to trigger animations
const isWidgetOpen = computed(() => store.getters['appConfig/getIsWidgetOpen']);

const runEnterAnimations = () => {
  let delay = 0;

  if (buttonRef.value) {
    animateEnter(buttonRef.value, delay);
    delay += STAGGER_DELAY;
  }

  if (iconRef.value) {
    animateEnter(iconRef.value, delay);
  }
};

watch(
  isWidgetOpen,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      runEnterAnimations();
    }
  },
  { immediate: true }
);

const buildOrderTrackingForm = () => {
  store.dispatch('conversation/buildOrderTrackingForm');
  emit('start-conversation');
};
</script>

<template>
  <section
    data-section="order-tracking"
    data-component="OrderTracking"
    data-version="2"
    class="px-4 py-2 rounded-lg bg-white"
    style="box-shadow: 0 1.004px 3.012px 0 rgba(20, 4, 40, 0.08)"
  >
    <div class="flex items-center justify-between">
      <div ref="buttonRef">
        <CsWidgetButton
          size="lg"
          :bg-color="widgetColor"
          :text-color="textColor"
          @click="buildOrderTrackingForm"
        >
          {{ $t('TRACK_ORDER') }}
        </CsWidgetButton>
      </div>
      <div
        ref="iconRef"
        class="w-8 h-8 rounded-lg flex items-center justify-center flex-none"
        :style="{ backgroundColor: iconBackgroundColor }"
      >
        <CsIcon name="package" :style="{ color: readableTextColor }" />
      </div>
    </div>
  </section>
</template>
